import { A, Location, RouteSectionProps, useNavigate } from "@solidjs/router";
import { castTimelineStream } from "applesauce-common/observable";
import { LatLngBounds, type LatLng } from "leaflet";
import ngeohash from "ngeohash";
import { createEffect, createMemo, createSignal, from } from "solid-js";
import WifiMap from "./components/wifi-map";

import { WifiNetwork } from "../../casts/wifi-network";
import {
  ListIcon,
  PlusIcon,
  QrCodeIcon,
  SearchIcon,
  UserIcon,
} from "../../components/icons";
import UserAvatar from "../../components/user-avatar";
import { WIFI_NETWORK_KIND } from "../../const";
import { accounts } from "../../services/accounts";
import { activeMailboxes } from "../../services/lifestyle";
import {
  getTimelineLoader,
  LOADERS_PRECISION,
} from "../../services/location-loaders";
import {
  defaultRelays,
  homeMapCenter,
  localMeshRelayEnabled,
  withLocalMeshRelay,
} from "../../services/settings";
import { eventStore } from "../../services/stores";

const LOADER_MAX_ZOOM = 13;

function HomeView(props: RouteSectionProps) {
  const location: Location<{ center?: LatLng }> = props.location;

  // set initial map center from location state
  if (location.state?.center) {
    homeMapCenter.next({
      center: location.state.center,
      zoom: 13,
    });
  }

  const account = from(accounts.active$);
  const navigate = useNavigate();

  const [zoom, setZoom] = createSignal(0);
  const [center, setCenter] = createSignal<LatLng | undefined>(undefined);
  const [geohashes, setGeohashes] = createSignal<string[]>([]);

  const shouldLoad = createMemo(() => zoom() >= LOADER_MAX_ZOOM);

  const onBBoxChange = (bbox: LatLngBounds) => {
    if (!shouldLoad()) return;

    const lat = [bbox.getSouth(), bbox.getNorth()];
    const lng = [bbox.getWest(), bbox.getEast()];

    const geohashes = ngeohash.bboxes(
      Math.min(...lat),
      Math.min(...lng),
      Math.max(...lat),
      Math.max(...lng),
      LOADERS_PRECISION,
    );
    setGeohashes(geohashes);
  };

  const mailboxes = from(activeMailboxes);
  const meshEnabled = from(localMeshRelayEnabled);

  createEffect(() => {
    geohashes();
    meshEnabled();
    const relays = withLocalMeshRelay(
      mailboxes()?.outboxes || defaultRelays.getValue(),
    );

    if (!shouldLoad()) return;

    // trigger loaders when geohashes change
    console.log(`Loading events for`, geohashes());

    for (const geohash of geohashes()) {
      getTimelineLoader(geohash, relays)().subscribe();
    }
  });

  const networks = from(
    eventStore
      .timeline({ kinds: [WIFI_NETWORK_KIND] })
      .pipe(castTimelineStream(WifiNetwork, eventStore)),
  );

  return (
    <>
      <div class="h-dvh bg-base-200 flex flex-col overflow-hidden">
        <main class="flex-col flex-grow flex">
          <WifiMap
            class="grow"
            networks={networks()}
            onBBoxChange={onBBoxChange}
            onZoomChange={setZoom}
            onCenterChange={setCenter}
          />
        </main>
        <footer class="bg-primary text-primary-content p-2 pb-safe-or-2 flex items-center gap-2">
          {account() ? (
            <A href="/profile">
              <UserAvatar pubkey={account()!.pubkey} class="bg-base-100" />
            </A>
          ) : (
            <A href="/welcome" class="btn btn-ghost btn-square">
              <UserIcon />
            </A>
          )}
          <A href="/search" class="btn btn-ghost btn-square">
            <SearchIcon />
          </A>
          <A href="/list" class="btn btn-ghost btn-square">
            <ListIcon />
          </A>
          <div class="flex-grow"></div>
          <A href="/scan" class="btn btn-ghost btn-square">
            <QrCodeIcon />
          </A>
          <button
            class="btn btn-ghost btn-square"
            onClick={() =>
              navigate("/create", {
                state: { center: center(), zoom: zoom() },
              })
            }
            aria-label="create"
          >
            <PlusIcon />
          </button>
        </footer>
      </div>
    </>
  );
}

export default HomeView;
